import {
  AlertTriangle,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  adminProductsV3Service,
  createAdminBasketRuntimeService,
  createAdminPortfolioRuntimeService,
} from "../../../services/adminProductsV3Service";
import {
  ActiveContractCompositionEntry,
  PortfolioActiveComposition,
} from "../../../types/productV3";
import { BASKET_MIN_CASH_CONID } from "../../../utils/basketMinInvestment";
import MinInvestmentAnalysis from "../../provider/shared/components/MinInvestmentAnalysis";
import {
  EnrichedBasketEntry,
  useBasketMinAnalyzer,
} from "./hooks/useBasketMinAnalyzer";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ProductType = "basket" | "portfolio";

interface SelectedProduct {
  providerId: number;
  productId: number;
  productType: ProductType;
  title: string;
  composition: ActiveContractCompositionEntry[];
  optionConIds: Set<number>;
}

// ---------------------------------------------------------------------------
// Portfolio flattening helper
// ---------------------------------------------------------------------------

async function flattenPortfolioComposition(
  composition: PortfolioActiveComposition,
  basketService: ReturnType<typeof createAdminBasketRuntimeService>,
  mode: "active" | "afterActivation" = "active"
): Promise<{ entries: ActiveContractCompositionEntry[]; optionConIds: Set<number> }> {
  const directStocks = composition.contracts.stocks ?? [];
  const directOptions = composition.contracts.options ?? [];
  const basketRefs = composition.baskets ?? [];

  const basketResults = await Promise.all(
    basketRefs.map(async (b) => {
      const stocks =
        mode === "afterActivation"
          ? (await basketService.getAfterActivation(b.basketId)).composition.contracts.stocks ?? []
          : (await basketService.getActiveComposition(b.basketId)).contracts.stocks ?? [];
      return { basketId: b.basketId, basketWeight: b.weight, stocks };
    })
  );

  const stockMap = new Map<number, ActiveContractCompositionEntry>();
  for (const s of directStocks) {
    if (s.conId === BASKET_MIN_CASH_CONID) continue;
    const existing = stockMap.get(s.conId);
    if (existing) {
      existing.weight += s.weight;
    } else {
      stockMap.set(s.conId, { ...s });
    }
  }
  for (const { basketWeight, stocks } of basketResults) {
    for (const s of stocks) {
      if (s.conId === BASKET_MIN_CASH_CONID) continue;
      const effectiveWeight = (basketWeight * s.weight) / 100;
      const existing = stockMap.get(s.conId);
      if (existing) {
        existing.weight += effectiveWeight;
      } else {
        stockMap.set(s.conId, { conId: s.conId, weight: effectiveWeight, versionId: s.versionId, activatedAtUtc: s.activatedAtUtc });
      }
    }
  }

  const optionConIds = new Set<number>();
  const optionEntries: ActiveContractCompositionEntry[] = directOptions
    .filter((o) => o.conId !== BASKET_MIN_CASH_CONID)
    .map((o) => {
      optionConIds.add(o.conId);
      return o;
    });

  return { entries: [...Array.from(stockMap.values()), ...optionEntries], optionConIds };
}

function compositionsEqual(
  a: ActiveContractCompositionEntry[],
  b: ActiveContractCompositionEntry[]
): boolean {
  if (a.length !== b.length) return false;
  const key = (e: ActiveContractCompositionEntry) => `${e.conId}:${e.weight}`;
  const aKeys = new Set(a.map(key));
  for (const entry of b) {
    if (!aKeys.has(key(entry))) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Top-level page
// ---------------------------------------------------------------------------

type CompositionView = "active" | "afterActivation";

const BasketMinAnalyzerPage: React.FC = () => {
  const {
    productType: productTypeParam,
    providerId: providerIdParam,
    productId: productIdParam,
  } = useParams<{ productType: string; providerId: string; productId: string }>();
  const navigate = useNavigate();

  const productType: ProductType | null = useMemo(
    () => (productTypeParam === "basket" || productTypeParam === "portfolio" ? productTypeParam : null),
    [productTypeParam]
  );
  const { providerId, productId } = useMemo(() => {
    const p = Number(providerIdParam);
    const b = Number(productIdParam);
    return {
      providerId: Number.isFinite(p) && p > 0 ? p : null,
      productId: Number.isFinite(b) && b > 0 ? b : null,
    };
  }, [providerIdParam, productIdParam]);

  const basketService = useMemo(
    () => (providerId != null ? createAdminBasketRuntimeService(providerId) : null),
    [providerId]
  );
  const portfolioService = useMemo(
    () => (providerId != null ? createAdminPortfolioRuntimeService(providerId) : null),
    [providerId]
  );

  const [selected, setSelected] = useState<SelectedProduct | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedError, setSelectedError] = useState<string | null>(null);
  const [view, setView] = useState<CompositionView>("active");
  const [afterActivation, setAfterActivation] = useState<
    ActiveContractCompositionEntry[] | null
  >(null);
  const [afterActivationOptionConIds, setAfterActivationOptionConIds] = useState<Set<number>>(new Set());
  const [afterActivationLoading, setAfterActivationLoading] = useState(false);
  const [afterActivationError, setAfterActivationError] = useState<string | null>(
    null
  );

  // Load product title + active composition via admin endpoints.
  // Baskets:     detail + /active
  // Portfolios:  detail + /active, then fan-out basket /active for underlying stocks, then flatten.
  useEffect(() => {
    if (providerId == null || productId == null || productType == null || !basketService) {
      setSelected(null);
      setSelectedError(null);
      setSelectedLoading(false);
      return;
    }
    let cancelled = false;
    setSelectedLoading(true);
    setSelectedError(null);
    setSelected(null);
    setView("active");

    const load = async () => {
      if (productType === "basket") {
        const [detail, active] = await Promise.all([
          adminProductsV3Service.getBasketDetail(providerId, productId),
          basketService.getActiveComposition(productId),
        ]);
        if (cancelled) return;
        const stocks = (active.contracts.stocks ?? []).filter(
          (s) => s.conId !== BASKET_MIN_CASH_CONID
        );
        setSelected({
          providerId,
          productId,
          productType: "basket",
          title: detail.basket.title,
          composition: stocks,
          optionConIds: new Set(),
        });
      } else {
        if (!portfolioService) return;
        const [detail, active] = await Promise.all([
          adminProductsV3Service.getPortfolioDetail(providerId, productId),
          portfolioService.getActiveComposition(productId),
        ]);
        if (cancelled) return;
        const { entries, optionConIds } = await flattenPortfolioComposition(active, basketService);
        if (cancelled) return;
        setSelected({
          providerId,
          productId,
          productType: "portfolio",
          title: detail.portfolio.title,
          composition: entries,
          optionConIds,
        });
      }
    };

    load().catch((err) => {
      if (cancelled) return;
      setSelectedError(err instanceof Error ? err.message : "Failed to load product");
    }).finally(() => {
      if (!cancelled) setSelectedLoading(false);
    });

    return () => { cancelled = true; };
  }, [providerId, productId, productType, basketService, portfolioService]);

  // After-activation composition via admin endpoints.
  // Baskets:     /after-activation → extract stocks
  // Portfolios:  /after-activation → flatten (same as active flow)
  useEffect(() => {
    if (!selected || !basketService) {
      setAfterActivation(null);
      setAfterActivationOptionConIds(new Set());
      setAfterActivationError(null);
      setAfterActivationLoading(false);
      setView("active");
      return;
    }
    let cancelled = false;
    setAfterActivationLoading(true);
    setAfterActivationError(null);

    const load = async () => {
      if (selected.productType === "basket") {
        const res = await basketService.getAfterActivation(selected.productId);
        if (cancelled) return;
        const stocks = (res.composition.contracts.stocks ?? []).filter(
          (s) => s.conId !== BASKET_MIN_CASH_CONID
        );
        setAfterActivation(stocks);
        setAfterActivationOptionConIds(new Set());
      } else {
        if (!portfolioService) return;
        const res = await portfolioService.getAfterActivation(selected.productId);
        if (cancelled) return;
        const { entries, optionConIds } = await flattenPortfolioComposition(res.composition, basketService, "afterActivation");
        if (cancelled) return;
        setAfterActivation(entries);
        setAfterActivationOptionConIds(optionConIds);
      }
    };

    load().catch((err) => {
      if (cancelled) return;
      setAfterActivation(null);
      setAfterActivationOptionConIds(new Set());
      setAfterActivationError(err instanceof Error ? err.message : "After-activation unavailable");
    }).finally(() => {
      if (!cancelled) setAfterActivationLoading(false);
    });

    return () => { cancelled = true; };
  }, [selected, basketService, portfolioService]);

  const hasAfterActivation =
    afterActivation != null &&
    !compositionsEqual(afterActivation, selected?.composition ?? []);

  const effectiveView: CompositionView =
    view === "afterActivation" && hasAfterActivation ? "afterActivation" : "active";

  const composition =
    effectiveView === "afterActivation"
      ? (afterActivation ?? null)
      : (selected?.composition ?? null);

  const EMPTY_OPTION_SET = useMemo(() => new Set<number>(), []);
  const activeOptionConIds =
    effectiveView === "afterActivation"
      ? afterActivationOptionConIds
      : (selected?.optionConIds ?? EMPTY_OPTION_SET);

  const { entries, loading, error, reload } = useBasketMinAnalyzer(composition, activeOptionConIds);

  const backToProducts = () => navigate("/admin/products");

  const typeLabel = productType === "portfolio" ? "Portfolio" : "Basket";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black text-gray-900 dark:text-white transition-colors duration-300">
      <div className="w-full px-3 py-3 md:px-6 md:py-5 space-y-3 md:space-y-4">
        <div className="flex items-start md:items-center justify-between gap-3 md:gap-6">
          <div className="min-w-0">
            <h1 className="text-base md:text-lg font-bold tracking-tight text-gray-900 dark:text-white">
              {typeLabel} min investment analyzer
            </h1>
            <p className="hidden md:block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Inspect {typeLabel.toLowerCase()} active composition, model scenarios.
            </p>
          </div>
          <button
            type="button"
            onClick={backToProducts}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 text-xs rounded-lg transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-3 h-3" />
            <span className="hidden sm:inline">Back to products</span>
            <span className="sm:hidden">Products</span>
          </button>
        </div>

        {selectedLoading && (
          <div className="flex items-center justify-center py-16 bg-white dark:bg-rp-surface rounded-xl border border-gray-200 dark:border-rp-separator">
            <Loader2 className="w-6 h-6 animate-spin text-rp-amber" />
            <span className="ml-3 text-sm text-gray-600 dark:text-gray-300">
              Loading {typeLabel.toLowerCase()} #{productId}...
            </span>
          </div>
        )}

        {!selectedLoading && selectedError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-red-700 dark:text-red-400">
                  Could not load {typeLabel.toLowerCase()}
                </div>
                <div className="text-xs text-red-600 dark:text-red-400/80 mt-0.5">
                  {selectedError}
                </div>
                <button
                  type="button"
                  onClick={backToProducts}
                  className="mt-2 text-xs text-red-700 dark:text-red-400 underline"
                >
                  Back to products
                </button>
              </div>
            </div>
          </div>
        )}

        {!selectedLoading && !selectedError && selected && (
          <>
            <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-2 md:gap-4">
              <div className="flex-1 min-w-0">
                <SelectedProductHeader product={selected} entries={entries} />
              </div>
              <CompositionViewToggle
                view={effectiveView}
                onChange={setView}
                afterActivationAvailable={hasAfterActivation}
                afterActivationLoading={afterActivationLoading}
                afterActivationError={afterActivationError}
                noPendingChanges={
                  afterActivation != null &&
                  !afterActivationLoading &&
                  !hasAfterActivation
                }
              />
            </div>

            {loading && (
              <div className="flex items-center justify-center py-16 bg-white dark:bg-rp-surface rounded-xl border border-gray-200 dark:border-rp-separator">
                <Loader2 className="w-6 h-6 animate-spin text-rp-amber" />
                <span className="ml-3 text-sm text-gray-600 dark:text-gray-300">
                  Loading active composition...
                </span>
              </div>
            )}

            {!loading && error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-red-700 dark:text-red-400">
                      Failed to load composition
                    </div>
                    <div className="text-xs text-red-600 dark:text-red-400/80 mt-0.5">
                      {error}
                    </div>
                    <button
                      type="button"
                      onClick={reload}
                      className="mt-2 text-xs text-red-700 dark:text-red-400 underline"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!loading && !error && entries.length > 0 && (
              <MinInvestmentAnalysis entries={entries} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BasketMinAnalyzerPage;

// ---------------------------------------------------------------------------
// Selected product header strip
// ---------------------------------------------------------------------------

interface SelectedProductHeaderProps {
  product: SelectedProduct;
  entries: EnrichedBasketEntry[];
}

const SelectedProductHeader: React.FC<SelectedProductHeaderProps> = ({
  product,
  entries,
}) => {
  const missingMeta = entries.filter(
    (e) => e.minSize == null || e.nativePrice == null
  );
  const missingFx = entries.filter(
    (e) => e.currency !== "USD" && e.fxRate == null
  );
  const nonUsdCurrencies = Array.from(
    new Set(
      entries
        .map((e) => e.currency)
        .filter((c) => c !== "USD" && c.length > 0)
    )
  );
  const optionCount = entries.filter((e) => e.isOption).length;
  const stockCount = entries.length - optionCount;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm min-w-0">
      <span className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-gray-500">
        {product.productType === "portfolio" ? "Portfolio" : "Basket"}
      </span>
      <span className="font-semibold text-gray-900 dark:text-white truncate max-w-full">
        {product.title}
      </span>
      <span className="text-[11px] text-gray-400 dark:text-gray-500 font-mono">
        #{product.productId}
      </span>
      <span className="text-[11px] text-gray-400 dark:text-gray-500 font-mono">
        provider #{product.providerId}
      </span>
      <span className="text-xs text-gray-400 dark:text-gray-500">
        {stockCount} stock{stockCount === 1 ? "" : "s"}
        {optionCount > 0 && ` + ${optionCount} option${optionCount === 1 ? "" : "s"}`}
        {product.productType === "portfolio" && " (flattened)"}
      </span>
      {nonUsdCurrencies.length > 0 && (
        <span
          className="text-[10px] text-blue-600 dark:text-blue-400 font-medium"
          title={`Prices converted to USD: ${nonUsdCurrencies.join(", ")}`}
        >
          FX: {nonUsdCurrencies.join(", ")}
        </span>
      )}
      {missingMeta.length > 0 && (
        <span className="text-[10px] text-amber-600 dark:text-amber-500">
          {missingMeta.length} missing price/lot
        </span>
      )}
      {missingFx.length > 0 && (
        <span className="text-[10px] text-amber-600 dark:text-amber-500">
          {missingFx.length} missing FX
        </span>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Composition view toggle -- active vs after-activation
// ---------------------------------------------------------------------------

interface CompositionViewToggleProps {
  view: CompositionView;
  onChange: (next: CompositionView) => void;
  afterActivationAvailable: boolean;
  afterActivationLoading: boolean;
  afterActivationError: string | null;
  noPendingChanges: boolean;
}

const CompositionViewToggle: React.FC<CompositionViewToggleProps> = ({
  view,
  onChange,
  afterActivationAvailable,
  afterActivationLoading,
  afterActivationError,
  noPendingChanges,
}) => {
  const afterDisabled = !afterActivationAvailable;
  const afterTitle = afterActivationLoading
    ? "Loading after-activation composition..."
    : afterActivationError
      ? `After-activation unavailable: ${afterActivationError}`
      : noPendingChanges
        ? "No pending changes -- same as active"
        : "Composition that will be live once all pending activations are applied";

  return (
    <div className="inline-flex items-center gap-2 md:ml-auto">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        View
      </span>
      <div className="inline-flex rounded-md border border-gray-200 dark:border-rp-separator overflow-hidden">
        <button
          type="button"
          onClick={() => onChange("active")}
          className={`px-3 py-1 text-xs font-medium transition-colors ${
            view === "active"
              ? "bg-rp-amber text-black"
              : "bg-white dark:bg-rp-surface text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-rp-surface-raised"
          }`}
        >
          Active
        </button>
        <button
          type="button"
          onClick={() => onChange("afterActivation")}
          disabled={afterDisabled}
          title={afterTitle}
          className={`px-3 py-1 text-xs font-medium transition-colors border-l border-gray-200 dark:border-rp-separator ${
            view === "afterActivation"
              ? "bg-rp-amber text-black"
              : afterDisabled
                ? "bg-gray-50 dark:bg-rp-surface-sunken text-gray-400 dark:text-gray-600 cursor-not-allowed"
                : "bg-white dark:bg-rp-surface text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-rp-surface-raised"
          }`}
        >
          After activation
          {afterActivationLoading && (
            <Loader2 className="inline-block w-3 h-3 ml-1 animate-spin" />
          )}
          {noPendingChanges && !afterActivationLoading && (
            <span className="ml-1 text-[10px] text-gray-400 dark:text-gray-500">
              (none)
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

/* Presentational analysis UI lives in MinInvestmentAnalysis (shared). */
